import { apiService } from '../../../services/apiService';
import { API_PATHS, getDeleteFraudBlocklistPath } from '../../../utils/apiPaths';
import type {
  FraudBlocklistEntry,
  FraudBlocklistPayload,
  FraudDecisionRow,
  FraudSummary,
  PagedResult,
} from '../models/types';

function unwrap<T>(res: any, fallback: T): T {
  if (!res?.success) throw new Error(res?.message || 'Request failed.');
  return (res.data ?? fallback) as T;
}

const EMPTY_PAGE = { content: [], totalElements: 0, totalPages: 0, page: 0, size: 0 };

export async function fetchFraudDecisions(params: {
  merchantId?: number | null;
  decision?: string;
  search?: string;
  days: number;
  page: number;
  size: number;
}): Promise<PagedResult<FraudDecisionRow>> {
  const query = new URLSearchParams();
  if (params.merchantId) query.set('merchantId', String(params.merchantId));
  if (params.decision && params.decision !== 'ALL') query.set('decision', params.decision);
  if (params.search) query.set('search', params.search);
  query.set('days', String(params.days));
  query.set('page', String(params.page));
  query.set('size', String(params.size));
  const res: any = await apiService.get(`${API_PATHS.GET_FRAUD_DECISIONS}?${query.toString()}`);
  return unwrap<PagedResult<FraudDecisionRow>>(res, EMPTY_PAGE as PagedResult<FraudDecisionRow>);
}

export async function fetchFraudSummary(days: number, merchantId?: number | null): Promise<FraudSummary> {
  const query = new URLSearchParams({ days: String(days) });
  if (merchantId) query.set('merchantId', String(merchantId));
  const res: any = await apiService.get(`${API_PATHS.GET_FRAUD_SUMMARY}?${query.toString()}`);
  return unwrap<FraudSummary>(res, {
    total: 0, allowed: 0, review: 0, blocked: 0, interventionRate: 0, shadowMode: true, topReasons: [],
  });
}

export async function fetchBlocklist(params: { entryType?: string; search?: string; page: number; size: number; }):
  Promise<PagedResult<FraudBlocklistEntry>> {
  const query = new URLSearchParams();
  if (params.entryType && params.entryType !== 'ALL') query.set('entryType', params.entryType);
  if (params.search) query.set('search', params.search);
  query.set('page', String(params.page));
  query.set('size', String(params.size));
  const res: any = await apiService.get(`${API_PATHS.GET_FRAUD_BLOCKLIST}?${query.toString()}`);
  return unwrap<PagedResult<FraudBlocklistEntry>>(res, EMPTY_PAGE as PagedResult<FraudBlocklistEntry>);
}

export async function addBlocklistEntry(payload: FraudBlocklistPayload): Promise<FraudBlocklistEntry> {
  const res: any = await apiService.post(API_PATHS.ADD_FRAUD_BLOCKLIST_ENTRY, payload);
  return unwrap<FraudBlocklistEntry>(res, {} as FraudBlocklistEntry);
}

export async function removeBlocklistEntry(id: number): Promise<void> {
  const res: any = await apiService.delete(getDeleteFraudBlocklistPath(id));
  if (!res?.success) throw new Error(res?.message || 'Request failed.');
}

export async function fetchFraudMode(): Promise<boolean> {
  const res: any = await apiService.get(API_PATHS.FRAUD_MODE);
  return Boolean(unwrap<{ enforcing?: boolean }>(res, {}).enforcing);
}

/** Flips shadow/enforce mode. Returns the mode now in effect (enforcing = true). */
export async function setFraudMode(enforce: boolean): Promise<boolean> {
  const res: any = await apiService.put(API_PATHS.FRAUD_MODE, { enforce });
  return Boolean(unwrap<{ enforcing?: boolean }>(res, { enforcing: enforce }).enforcing);
}
